// Single source of truth for site SEO metadata + service / canton configurations.

export const SITE_URL = "https://gomopper.ch";
export const SITE_NAME = "Mopper";
// Official Mopper logo (used in Header, Footer, emails, JSON-LD, OG share).
export const MOPPER_LOGO_URL = "https://gomopper.ch/branding/logo-blue.png";
export const DEFAULT_OG_IMAGE = MOPPER_LOGO_URL;

// 8 services — slug used in URL `/services/{slug}` and in JSON-LD
export const SERVICES = [
  {
    slug: "demenagement",
    key: "move",
    name: "Déménagement",
    h1: "Déménagement clé en main en Suisse romande",
    tagline: "Une équipe, un camion, un seul interlocuteur — du carton au remontage.",
    metaTitle: "Déménagement clé en main en Suisse romande | Mopper",
    metaDescription: "Mopper organise votre déménagement en Suisse romande : protection, chargement, transport, démontage et remontage de meubles. Devis gratuit.",
    intro: "Mopper organise votre déménagement en Suisse romande de A à Z : protection des biens, manutention, démontage et remontage des meubles, transport et nettoyage fin de bail si besoin.",
    forWho: [
      "Particuliers (studio, appartement, villa)",
      "Familles avec enfants",
      "Personnes âgées et successions",
      "Entreprises, bureaux et locaux commerciaux",
      "Régies immobilières et propriétaires",
    ],
    useCases: [
      "Déménagement appartement 2.5–6.5 pièces à Lausanne ou Genève",
      "Déménagement villa avec cave, garage et grenier",
      "Déménagement de bureau ou de cabinet",
      "Déménagement combiné avec nettoyage fin de bail",
      "Petit déménagement express (studio, étudiant)",
    ],
    advantages: [
      "Équipe formée à la manutention et à la protection des biens",
      "Véhicules adaptés (3.5 t à 16 t selon volume)",
      "Couvertures, sangles, films plastiques, EPI fournis",
      "Démontage et remontage des meubles inclus sur demande",
      "Cartons fournis, possibilité d'emballage complet",
      "Nettoyage fin de bail en option dans la même prestation",
    ],
    options: [
      "Fourniture et reprise de cartons",
      "Démontage / remontage IKEA et meubles complexes",
      "Garde-meuble temporaire",
      "Nettoyage fin de bail + état des lieux",
      "Débarras des objets non emportés",
    ],
    keywords: ["déménagement Suisse romande", "déménagement Lausanne", "déménagement Genève", "entreprise de déménagement Vaud", "déménagement et nettoyage"],
  },
  {
    slug: "debarras",
    key: "clear",
    name: "Débarras",
    h1: "Débarras complet et responsable, partout en Suisse romande",
    tagline: "On vide, on trie, on donne, on recycle. Vous récupérez un espace prêt à vivre.",
    metaTitle: "Débarras d'appartement, maison et cave | Mopper",
    metaDescription: "Mopper vide appartements, maisons, caves, greniers et locaux en Suisse romande. Tri, évacuation et recyclage selon vos besoins.",
    intro: "Mopper vide votre appartement, cave, grenier, garage ou maison entière. On trie, on donne ce qui peut l'être, on recycle, et on évacue le reste proprement. Une seule équipe, un seul devis.",
    forWho: [
      "Propriétaires avant vente ou mise en location",
      "Successions et débarras après décès",
      "Particuliers qui veulent libérer de l'espace",
      "Régies immobilières et gérances",
      "Entreprises qui ferment ou déménagent",
    ],
    useCases: [
      "Vider un appartement après un décès",
      "Vider une cave ou un grenier encombré à Lausanne",
      "Vider une maison avant vente immobilière",
      "Débarras d'un garage ou d'un local de stockage",
      "Débarras combiné avec nettoyage fin de bail",
    ],
    advantages: [
      "Tri responsable : don, recyclage, revalorisation",
      "Évacuation rapide en quelques jours",
      "Frais de déchèterie inclus dans le devis",
      "Aucun objet abandonné, aucune trace sur place",
      "Discrétion totale pour les successions sensibles",
    ],
    options: [
      "Nettoyage après débarras",
      "Tri sélectif avancé (papiers, archives, électroménager)",
      "Recherche d'objets de valeur avant évacuation",
      "Démontage de meubles encombrants",
    ],
    keywords: ["débarras appartement Lausanne", "débarras maison Vaud", "débarras cave", "débarras grenier", "débarras garage", "entreprise de débarras Suisse romande", "vider appartement succession", "vider maison avant vente"],
  },
  {
    slug: "desencombrement",
    key: "declutter",
    hidden: true,
    name: "Désencombrement",
    h1: "Désencombrement accompagné, sans pression",
    tagline: "Un accompagnement humain pour alléger votre intérieur — vous décidez, on évacue.",
    metaTitle: "Désencombrement de maison et appartement | Mopper",
    metaDescription: "Mopper vous accompagne pour trier, alléger et réorganiser votre maison ou appartement en Suisse romande.",
    intro: "Le désencombrement Mopper est plus doux qu'un simple débarras. On vous accompagne pour trier, garder l'essentiel et libérer de l'espace, sans jamais brusquer.",
    forWho: [
      "Particuliers qui se sentent débordés par leurs objets",
      "Personnes qui changent de vie (déménagement, séparation, retraite)",
      "Familles qui rangent après une période chargée",
      "Personnes accompagnant un parent âgé",
    ],
    useCases: [
      "Désencombrer une maison familiale chargée d'années de souvenirs",
      "Trier un appartement avant un déménagement à Lausanne",
      "Désencombrer un dressing, une cave, un grenier",
      "Préparer un logement pour la mise en vente",
    ],
    advantages: [
      "Méthode douce et respectueuse de vos objets",
      "Accompagnement humain à chaque étape",
      "Tri en 4 catégories : garder, donner, vendre, jeter",
      "Conseils de rangement adaptés à votre intérieur",
      "Recyclage et dons gérés par notre équipe",
    ],
    options: [
      "Home Organizing complémentaire (rangement, optimisation)",
      "Évacuation et recyclage des objets triés",
      "Accompagnement séance par séance",
    ],
    keywords: ["désencombrement maison", "désencombrement appartement", "désencombrement Lausanne"],
  },
  {
    slug: "home-organizing",
    key: "organize",
    hidden: true,
    name: "Home Organizing",
    h1: "Home Organizing — vos espaces pensés pour la vraie vie",
    tagline: "Dressing, cuisine, bureau, chambres : un système simple, durable, qui vous ressemble.",
    metaTitle: "Home organizing à Lausanne et dans le canton de Vaud | Mopper",
    metaDescription: "Organisation de dressing, cuisine, bureau et espaces de vie à Lausanne et dans le canton de Vaud avec Mopper.",
    intro: "Le Home Organizing Mopper, c'est l'art d'organiser vos espaces pour qu'ils restent fonctionnels et agréables au quotidien. Dressing, cuisine, bureau, chambre d'enfant : on optimise tout.",
    forWho: [
      "Particuliers qui veulent un intérieur plus fonctionnel",
      "Familles débordées par l'organisation du quotidien",
      "Indépendants qui veulent un home office efficace",
      "Personnes emménageant dans un nouveau logement",
    ],
    useCases: [
      "Organiser un dressing à Lausanne après un déménagement",
      "Optimiser une petite cuisine genevoise",
      "Aménager un bureau à domicile",
      "Ranger une chambre d'enfant",
      "Préparer un logement pour la location courte durée",
    ],
    advantages: [
      "Diagnostic personnalisé de votre espace",
      "Solutions de rangement adaptées à votre budget",
      "Méthode éprouvée, résultat durable",
      "Conseils d'entretien pour ne pas re-encombrer",
      "Achats de rangements possibles (en option)",
    ],
    options: [
      "Désencombrement préalable",
      "Achat et installation de rangements (IKEA, Elfa, Pax)",
      "Étiquetage et système d'organisation visuel",
      "Suivi à 1 mois pour ajuster",
    ],
    keywords: ["home organizing Suisse", "home organizing Lausanne", "organisation intérieure"],
  },
  {
    slug: "nettoyage",
    key: "clean",
    name: "Nettoyage",
    h1: "Nettoyage fin de bail avec garantie état des lieux",
    tagline: "Checklist régies suisses, vitres, électroménager, joints — rien n'est laissé au hasard.",
    metaTitle: "Nettoyage de fin de bail et après déménagement | Mopper",
    metaDescription: "Nettoyage de fin de bail, après déménagement ou débarras en Suisse romande. Une seule équipe pour coordonner votre intervention.",
    intro: "Nettoyage fin de bail, nettoyage après déménagement, nettoyage après débarras : Mopper s'occupe de remettre vos espaces impeccables, avec garantie état des lieux.",
    forWho: [
      "Locataires en fin de bail",
      "Propriétaires entre deux locataires",
      "Régies immobilières",
      "Particuliers après un débarras ou un déménagement",
      "Entreprises et bureaux",
    ],
    useCases: [
      "Nettoyage fin de bail d'un appartement à Lausanne",
      "Nettoyage après déménagement à Genève",
      "Nettoyage après débarras (poussière, traces, résidus)",
      "Nettoyage avant emménagement",
      "Nettoyage de bureaux ou local commercial",
    ],
    advantages: [
      "Garantie de passage état des lieux (avec gérance)",
      "Vitres intérieures et extérieures incluses",
      "Électroménager nettoyé en profondeur (four, frigo, hotte)",
      "Sanitaires détartrés, joints traités",
      "Produits adaptés et respectueux",
    ],
    options: [
      "Nettoyage tapis et moquettes",
      "Détartrage approfondi salle de bain",
      "Nettoyage cave / balcon / cheminée",
      "Évacuation des derniers déchets",
    ],
    keywords: ["nettoyage fin de bail Lausanne", "nettoyage après déménagement", "nettoyage après débarras", "nettoyage appartement état des lieux"],
  },
  {
    slug: "recyclage",
    key: "recycle",
    hidden: true,
    name: "Recyclage",
    h1: "Recyclage et don : la déchèterie en dernier recours",
    tagline: "Filières suisses, dons prioritaires, traçabilité possible. Évacuer mieux, et moins.",
    metaTitle: "Recyclage et évacuation responsable · Suisse romande | Mopper",
    metaDescription: "Service de recyclage et d'évacuation responsable en Suisse romande. Don, revalorisation, tri sélectif, transport en déchèterie. Devis gratuit.",
    intro: "Mopper évacue vos objets avec une vraie logique de tri : don aux associations, recyclage, revalorisation, déchèterie en dernier recours.",
    forWho: [
      "Particuliers après débarras ou désencombrement",
      "Entreprises avec matériel à recycler",
      "Régies et propriétaires",
      "Successions",
    ],
    useCases: [
      "Recyclage d'électroménager (frigo, lave-linge, four)",
      "Don de meubles en bon état à des associations",
      "Évacuation de meubles cassés en déchèterie",
      "Tri et évacuation d'archives papier",
      "Recyclage de matelas et literie",
    ],
    advantages: [
      "Partenariats avec associations suisses (Caritas, EMMAÜS)",
      "Tri sélectif systématique",
      "Frais de déchèterie inclus",
      "Traçabilité possible sur demande",
      "Logique zéro-déchet quand c'est possible",
    ],
    options: [
      "Photos avant don pour traçabilité",
      "Tri spécifique (papier, électronique, encombrants)",
      "Certificat de recyclage pour entreprises",
    ],
    keywords: ["recyclage Suisse romande", "évacuation déchèterie", "don meubles"],
  },
  {
    slug: "montage-de-meubles",
    key: "assembly",
    hidden: true,
    name: "Montage de meubles",
    h1: "Montage de meubles IKEA, dressing PAX et sur-mesure",
    tagline: "On monte, démonte, remonte, fixe au mur. Vous profitez du résultat.",
    metaTitle: "Montage et démontage de meubles | Mopper",
    metaDescription: "Montage, démontage et remontage de meubles à Lausanne, dans le canton de Vaud et en Suisse romande. Transport disponible en option.",
    intro: "Mopper monte vos meubles chez vous, rapidement et proprement. IKEA, dressings sur mesure, lits, armoires, bureaux, cuisines : on a l'outillage et l'expérience.",
    forWho: [
      "Particuliers qui viennent d'acheter un meuble",
      "Personnes pressées ou peu bricoleuses",
      "Personnes âgées",
      "Régies équipant un logement",
      "Entreprises équipant des bureaux",
    ],
    useCases: [
      "Montage d'un dressing PAX IKEA à Lausanne",
      "Montage d'un lit, d'une armoire, d'un bureau",
      "Équipement complet d'un appartement neuf",
      "Démontage et remontage lors d'un déménagement",
      "Montage de meubles de bureau pour entreprise",
    ],
    advantages: [
      "Équipe outillée (visseuses, niveaux, équerres)",
      "Lecture rapide des notices, montage propre",
      "Protection du sol et nettoyage après intervention",
      "Tarif transparent, devis sous 24h",
      "Évacuation des emballages incluse",
    ],
    options: [
      "Démontage des anciens meubles",
      "Fixation murale (étagères, télés, armoires)",
      "Achat et livraison du meuble en amont",
      "Mise en service d'électroménager",
    ],
    keywords: ["montage de meubles Lausanne", "montage meubles IKEA", "montage dressing PAX"],
  },
  {
    slug: "transport-de-meubles",
    key: "transport",
    name: "Transport de meubles",
    h1: "Transport de meubles en Suisse romande et au-delà",
    tagline: "Un canapé, dix meubles, un piano ou un studio entier — protection pro et livraison soignée.",
    metaTitle: "Transport de meubles en Suisse romande | Mopper",
    metaDescription: "Transport d'un meuble ou de plusieurs éléments en Suisse romande : protection, chargement, livraison et montage en option.",
    intro: "Transport d'un canapé, d'une armoire, d'une œuvre d'art, d'un piano ou d'un appartement complet : Mopper transporte vos biens en Suisse romande, dans toute la Suisse, et à l'international sur demande.",
    forWho: [
      "Particuliers (achats, ventes, échanges)",
      "Entreprises et bureaux",
      "Galeristes et artistes",
      "Antiquaires et brocanteurs",
      "Personnes en mobilité internationale",
    ],
    useCases: [
      "Transport d'un canapé acheté en ligne à Lausanne",
      "Transport d'une armoire entre deux appartements",
      "Transport d'un piano ou d'œuvres d'art",
      "Transport national vers Zurich, Berne, Bâle",
      "Transport international (France, Allemagne, Italie)",
    ],
    advantages: [
      "Véhicules adaptés et chauffeurs formés",
      "Protections, sangles, couvertures incluses",
      "Assurance sur les biens transportés",
      "Devis transparent (distance, volume, accès)",
      "Démontage et remontage possibles aux deux extrémités",
    ],
    options: [
      "Démontage / remontage des meubles",
      "Emballage et déballage",
      "Stockage temporaire (garde-meuble)",
      "Transport climatisé pour pièces sensibles",
    ],
    keywords: ["transport de meubles Suisse", "transport de meubles Lausanne", "transport national de meubles", "transport international de meubles"],
  },
];

// Services hidden from public UI (kept here so they can be re-enabled in 1 line).
// Hidden services are EXCLUDED from: nav, footer, home grid, services index, canton
// pages, quote form, sitemap, JSON-LD. Their routes /services/{slug} remain
// resolvable (no broken bookmarks) but are not advertised.
export const HIDDEN_SERVICE_KEYS = SERVICES.filter((s) => s.hidden).map((s) => s.key);
export const HIDDEN_SERVICE_SLUGS = SERVICES.filter((s) => s.hidden).map((s) => s.slug);
export const VISIBLE_SERVICES = SERVICES.filter((s) => !s.hidden);

// 6 cantons romands — pages locales `/zones/{slug}`
export const CANTONS_SEO = [
  {
    slug: "vaud",
    name: "Vaud",
    capital: "Lausanne",
    h1: "Déménagement, débarras et nettoyage dans le canton de Vaud",
    metaTitle: "Déménagement, débarras & nettoyage dans le canton de Vaud | Mopper",
    metaDescription: "Mopper intervient dans tout le canton de Vaud pour vos déménagements, débarras, nettoyages fin de bail, home organizing et transports de meubles.",
    cities: ["Lausanne", "Montreux", "Vevey", "Yverdon-les-Bains", "Morges", "Nyon", "Renens", "Pully", "Prilly"],
    intro: "Vaud est notre canton d'origine. De Lausanne à Yverdon-les-Bains, de Morges à Montreux, Mopper accompagne particuliers, entreprises et régies pour tout ce qui encombre leurs espaces.",
    localContext: "Le canton de Vaud combine une forte densité urbaine autour de l'arc lémanique avec des zones rurales. Mopper adapte ses interventions aux contraintes locales : appartements anciens du centre de Lausanne, villas avec jardin sur la Riviera, immeubles modernes à Morges ou Nyon.",
  },
  {
    slug: "geneve",
    name: "Genève",
    capital: "Genève",
    h1: "Déménagement, débarras et nettoyage dans le canton de Genève",
    metaTitle: "Déménagement, débarras & nettoyage · canton de Genève | Mopper",
    metaDescription: "Mopper intervient à Genève ville et dans tout le canton : déménagement, débarras, nettoyage fin de bail, home organizing, transport de meubles. Équipe locale, devis gratuit sous 24h.",
    cities: ["Genève", "Carouge", "Vernier", "Lancy", "Meyrin", "Onex", "Plan-les-Ouates", "Versoix"],
    intro: "Genève est l'un de nos terrains d'intervention prioritaires. Centre-ville historique, quartiers résidentiels, zones internationales : Mopper connaît les particularités du canton.",
    localContext: "À Genève, les contraintes d'accès (rues étroites, parkings limités, ascenseurs petits) demandent une vraie préparation. Mopper anticipe ces points pour livrer une intervention propre et rapide, à Carouge comme à Versoix.",
  },
  {
    slug: "fribourg",
    name: "Fribourg",
    capital: "Fribourg",
    h1: "Déménagement, débarras et nettoyage dans le canton de Fribourg",
    metaTitle: "Déménagement, débarras & nettoyage · canton de Fribourg | Mopper",
    metaDescription: "Mopper intervient dans le canton de Fribourg pour vos déménagements, débarras, nettoyages et transports de meubles.",
    cities: ["Fribourg", "Bulle", "Romont", "Estavayer-le-Lac", "Villars-sur-Glâne", "Marly"],
    intro: "De Fribourg à Bulle, en passant par Romont et Estavayer, Mopper intervient dans tout le canton bilingue pour vider, déménager, organiser et nettoyer.",
    localContext: "Fribourg combine ville universitaire animée et villages ruraux. Mopper s'adapte aux logements anciens du centre-ville, aux fermes rénovées de la Gruyère et aux maisons individuelles de la périphérie.",
  },
  {
    slug: "neuchatel",
    name: "Neuchâtel",
    capital: "Neuchâtel",
    h1: "Déménagement, débarras et nettoyage dans le canton de Neuchâtel",
    metaTitle: "Déménagement, débarras & nettoyage · canton de Neuchâtel | Mopper",
    metaDescription: "Mopper intervient à Neuchâtel, La Chaux-de-Fonds, Le Locle. Déménagement, débarras, nettoyage fin de bail, home organizing, transport de meubles. Devis gratuit.",
    cities: ["Neuchâtel", "La Chaux-de-Fonds", "Le Locle", "Boudry", "Val-de-Travers"],
    intro: "De Neuchâtel à La Chaux-de-Fonds, Mopper accompagne particuliers et entreprises pour libérer leurs espaces et simplifier leur quotidien.",
    localContext: "Le canton de Neuchâtel mêle bord du lac et Montagnes neuchâteloises. Mopper intervient aussi bien dans les immeubles classés du centre-ville que dans les maisons horlogères du Locle et de La Chaux-de-Fonds.",
  },
  {
    slug: "valais",
    name: "Valais",
    capital: "Sion",
    h1: "Déménagement, débarras et nettoyage en Valais",
    metaTitle: "Déménagement, débarras & nettoyage en Valais | Mopper",
    metaDescription: "Mopper intervient en Valais pour vos déménagements, débarras, nettoyages et transports de meubles.",
    cities: ["Sion", "Sierre", "Martigny", "Monthey", "Verbier", "Crans-Montana", "Saint-Maurice"],
    intro: "Sion, Sierre, Martigny, Verbier : Mopper se déplace dans tout le Valais romand, y compris les stations d'altitude, pour vider, déménager, transporter et organiser vos espaces.",
    localContext: "Le Valais demande une logistique adaptée : routes de montagne, accès saisonniers, chalets isolés. Mopper a l'expérience des transports en altitude et des interventions en stations.",
  },
  {
    slug: "jura",
    name: "Jura",
    capital: "Delémont",
    h1: "Déménagement, débarras et nettoyage dans le Jura",
    metaTitle: "Déménagement, débarras & nettoyage dans le Jura | Mopper",
    metaDescription: "Mopper intervient dans le Jura pour vos déménagements, débarras, nettoyages et transports de meubles.",
    cities: ["Delémont", "Porrentruy", "Saignelégier", "Bassecourt", "Courrendlin"],
    intro: "De Delémont à Porrentruy, Mopper accompagne le canton du Jura avec la même exigence que partout en Suisse romande.",
    localContext: "Le Jura est un canton rural avec des distances importantes. Mopper optimise ses tournées pour rester accessible côté tarif, et propose des prestations combinées (débarras + nettoyage, déménagement + transport) pour rentabiliser le déplacement.",
  },
];

// Default site-wide meta
export const DEFAULT_META = {
  title: "Déménagement, débarras & nettoyage en Suisse romande | Mopper",
  description: "Mopper simplifie vos déménagements, débarras, nettoyages, transports et organisations d'espaces en Suisse romande. Demandez votre devis gratuit.",
  ogImage: DEFAULT_OG_IMAGE,
};

// Build the LocalBusiness JSON-LD (global, attached to home page).
export function buildLocalBusinessLd() {
  return {
    "@context": "https://schema.org",
    "@type": "MovingCompany",
    "@id": `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    legalName: "Mopper Sàrl",
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,
    email: "hello@gomopper.ch",
    telephone: "+41782321850",
    priceRange: "CHF",
    address: { "@type": "PostalAddress", addressCountry: "CH", addressRegion: "Vaud", addressLocality: "Lausanne" },
    areaServed: CANTONS_SEO.map((c) => ({ "@type": "AdministrativeArea", name: c.name })),
    sameAs: [],
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "18:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Saturday"], opens: "09:00", closes: "16:00" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Services Mopper",
      itemListElement: VISIBLE_SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.name, url: `${SITE_URL}/services/${s.slug}` },
      })),
    },
  };
}

export function buildOrganizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    email: "hello@gomopper.ch",
    contactPoint: [{ "@type": "ContactPoint", telephone: "+41782321850", contactType: "customer service", availableLanguage: ["French", "German", "English"] }],
  };
}

export function buildServiceLd(service) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.metaDescription,
    provider: { "@id": `${SITE_URL}/#localbusiness` },
    serviceType: service.name,
    areaServed: CANTONS_SEO.map((c) => ({ "@type": "AdministrativeArea", name: c.name })),
    url: `${SITE_URL}/services/${service.slug}`,
  };
}

// Service JSON-LD ancré sur une localité réelle (areaServed = ville + canton).
// Même provider Mopper (@id stable) — aucune agence physique locale inventée.
export function buildLocalServiceLd({ serviceName, cityName, canton, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${serviceName} à ${cityName}`,
    description,
    provider: { "@id": `${SITE_URL}/#localbusiness` },
    serviceType: serviceName,
    areaServed: [
      { "@type": "City", name: cityName },
      { "@type": "AdministrativeArea", name: canton },
    ],
    url,
  };
}

export function buildBreadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.url })),
  };
}

export function buildFaqLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}
